// scorelines/data.js — data and wording shared by the scoreline pages
// (/scorelines and /scorelines/:score). Scorelines are keyed winner-first
// ("7-3"), like the API and the summary table.

import { useEffect, useState } from "react";
import { cachedScores, fetchScores } from "../../api";
import { displayName, formatDate, getCurrentSeasonStartYear, occurrenceLine, HOME_AWAY, seasonStartYear } from "../heatmap/utils";

// Every match with one scoreline (the API follows its pages up to 5,000).
export const scoreQuery = (key) => `score=${key}&limit=5000`;

// One API query, seeded from the pre-render cache when the page was saved
// with it, then refreshed in the background. A failed refresh keeps the
// seeded data. Callers key the component by query, so state never mixes
// two queries.
export function useSeededQuery(query) {
  const [state, setState] = useState(() => {
    const seeded = cachedScores(query);
    return seeded ? { data: seeded } : { loading: true };
  });
  useEffect(() => {
    let cancelled = false;
    fetchScores(query)
      .then((data) => !cancelled && setState({ data }))
      .catch(() => !cancelled && setState((s) => (s.data ? s : { error: true })));
    return () => {
      cancelled = true;
    };
  }, [query]);
  return state;
}

export const parseKey = (key) => key.split("-").map(Number);
export const fmtScore = (key) => key.replace("-", "–");
export const scorelinePath = (key) => `/scorelines/${key}`;
export const isDrawKey = (key) => {
  const [a, b] = parseKey(key);
  return a === b;
};

// "a 7–3" / "an 8–0" (for sentences).
export const withArticle = (key) => `${/^(8|11|18)-/.test(key) ? "an" : "a"} ${fmtScore(key)}`;

const SMALL = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];
// "six", "12": numbers under ten spelled out, for running text.
export const numberWord = (n) => SMALL[n] || n.toLocaleString();

export const countWord = (n) => (n === 1 ? "once" : n === 2 ? "twice" : `${n.toLocaleString()} times`);

// Home-first "Arsenal 7–3 Newcastle" for a summary occurrence.
export const occurrenceText = (occ) => occurrenceLine(occ, HOME_AWAY);
export const occurrenceWhen = (occ) => formatDate(occ.utcDate);

// Home-first line for a raw match from the API.
export const matchText = (m) =>
  `${displayName(m.homeTeam)} ${m.homeTeamScore}–${m.awayTeamScore} ${displayName(m.awayTeam)}`;

export const seasonLabel = (utcDate) => {
  const y = seasonStartYear(utcDate);
  return `${y}/${String((y + 1) % 100).padStart(2, "0")}`;
};

// Totals and ranks across the whole summary: { total, rows (by count, most
// common first), rankOf(key), oneOffs }.
export function summaryStats(items) {
  const rows = [...items].map((r) => ({ ...r, count: Number(r.count) })).sort((a, b) => b.count - a.count || a.score.localeCompare(b.score, "en", { numeric: true }));
  const total = rows.reduce((n, r) => n + r.count, 0);
  const rankOf = (key) => {
    const row = rows.find((r) => r.score === key);
    if (!row) return null;
    return { rank: 1 + rows.filter((r) => r.count > row.count).length, tied: rows.filter((r) => r.count === row.count).length };
  };
  return { rows, total, rankOf, oneOffs: rows.filter((r) => r.count === 1).length };
}

// Matches per season, from the first season to the current one (zeros kept).
export function bySeason(matches) {
  const counts = new Map();
  for (const m of matches) {
    const y = seasonStartYear(m.utcDate);
    counts.set(y, (counts.get(y) || 0) + 1);
  }
  const out = [];
  for (let y = 1992; y <= getCurrentSeasonStartYear(); y++) {
    out.push({ year: y, label: `${String(y % 100).padStart(2, "0")}/${String((y + 1) % 100).padStart(2, "0")}`, count: counts.get(y) || 0 });
  }
  return out;
}

// Clubs (stored team names) by how often they won / lost (or drew) by this
// scoreline: { won: [[team, n], …], lost: [[team, n], …] }, most first.
export function clubTallies(matches) {
  const won = new Map();
  const lost = new Map();
  const add = (map, club) => map.set(club, (map.get(club) || 0) + 1);
  for (const m of matches) {
    const hs = Number(m.homeTeamScore);
    const as = Number(m.awayTeamScore);
    if (hs === as) {
      add(won, m.homeTeam);
      add(won, m.awayTeam);
    } else {
      add(won, hs > as ? m.homeTeam : m.awayTeam);
      add(lost, hs > as ? m.awayTeam : m.homeTeam);
    }
  }
  const sorted = (map) => [...map.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  return { won: sorted(won), lost: sorted(lost) };
}

// Scorelines one goal away that have happened (winner-first keys).
export function neighbours(key, known) {
  const [a, b] = parseKey(key);
  const cands = [
    [a + 1, b],
    [a - 1, b],
    [a, b + 1],
    [a, b - 1],
  ]
    .filter(([x, y]) => x >= 0 && y >= 0)
    .map(([x, y]) => `${Math.max(x, y)}-${Math.min(x, y)}`);
  return [...new Set(cands)].filter((k) => k !== key && known.has(k));
}
